// ============================================================================
// aiIntelligence-renderer.js
//
// Renders the unified AI Intelligence JSON into self-contained HTML.
// Three parts rendered sequentially:
//   1. Opportunity cards  — Rovo product folded into each card
//   2. Implementation sequence
//   3. Target workflow state (outcome schematic)
// ============================================================================

const esc = (v = '') =>
  String(v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const embeddedJsonScript = (json) => `<script id="__qa_data__" type="application/json">${JSON.stringify(json || {}).replace(/</g, '\\u003C')}</script>`;

// ── Rovo product config ────────────────────────────────────────────────────────

const ROVO = {
  'Rovo Agents': { bg: '#E6F1FB', text: '#0052CC', border: '#85B7EB', dot: '#0052CC', label: 'Rovo Agents' },
  'Rovo Search': { bg: '#EAE6FF', text: '#5243AA', border: '#998DD9', dot: '#6554C0', label: 'Rovo Search' },
  'Rovo Chat':   { bg: '#E3FCEF', text: '#006644', border: '#57D9A3', dot: '#00875A', label: 'Rovo Chat'   },
  'Rovo Studio': { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380', dot: '#FF8B00', label: 'Rovo Studio' },
  'Jira Automation': { bg: '#E3FCEF', text: '#006644', border: '#57D9A3', dot: '#00875A', label: 'Jira Automation' },
  'Process Change': { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6', dot: '#6B778C', label: 'Process Change' },
};
const getRovo = (p) => ROVO[p] || { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6', dot: '#97A0AF', label: p || 'Rovo' };
const PLATFORM_PRODUCTS = {
  ...ROVO,
  'Now Assist': { bg: '#E3FCEF', text: '#006644', border: '#57D9A3', dot: '#00875A', label: 'Now Assist' },
  'Predictive Intelligence': { bg: '#EAE6FF', text: '#5243AA', border: '#998DD9', dot: '#6554C0', label: 'Predictive Intelligence' },
  'Virtual Agent': { bg: '#DEEBFF', text: '#0747A6', border: '#B3D4FF', dot: '#0052CC', label: 'Virtual Agent' },
  'Flow Designer': { bg: '#FFF0B3', text: '#974F0C', border: '#FFE380', dot: '#FF8B00', label: 'Flow Designer' },
  'Automation Engine': { bg: '#FFEBE6', text: '#BF2600', border: '#FF8F73', dot: '#BF2600', label: 'Automation Engine' },
  'Assignment Rules': { bg: '#E6FCFF', text: '#006C80', border: '#79E2F2', dot: '#00A3BF', label: 'Assignment Rules' },
  'SLA/Performance Analytics': { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6', dot: '#6B778C', label: 'SLA/Performance Analytics' },
};
const getPlatformProduct = (p) => PLATFORM_PRODUCTS[p] || { bg: '#F4F5F7', text: '#42526E', border: '#DFE1E6', dot: '#97A0AF', label: p || 'AI capability' };
const getPlatformMeta = (data = {}) => {
  const source = String(data.source_system || '').toLowerCase();
  const isSnow = source === 'servicenow' || source === 'snow';
  return {
    isSnow,
    platformName: data.platform_name || (isSnow ? 'ServiceNow' : 'Jira / Jira Service Management'),
    productLabel: isSnow ? 'ServiceNow / process capabilities' : 'Atlassian / process capabilities',
    opportunityHeading: data.opportunity_heading || 'AI, Automation & Optimisation Opportunities',
    readinessLabel: data.readiness_label || (isSnow ? 'AI Readiness' : 'Rovo Readiness'),
    defaultProduct: isSnow ? 'ServiceNow AI' : 'Rovo',
  };
};
const getReadiness = (data = {}) => data.readiness || data.rovo_readiness || data.summary?.rovo_readiness || 'Medium';
const getReadinessRationale = (data = {}) => data.readiness_rationale || data.rovo_readiness_rationale || data.summary?.rovo_readiness_rationale || '';

const getOpportunityType = (o = {}) => {
  const explicit = String(o.opportunity_type || '').trim().toLowerCase();
  if (explicit === 'ai') return 'AI';
  if (explicit === 'automation') return 'Automation';
  if (explicit === 'optimisation' || explicit === 'optimization') return 'Optimisation';
  const legacy = String(o.intervention_type || '').trim().toLowerCase();
  if (legacy === 'augment') return 'AI';
  if (legacy === 'automate') return 'Automation';
  return 'Optimisation';
};

const opportunityTypeBadge = (o = {}) => {
  const t = getOpportunityType(o);
  const cfg = {
    AI: ['#EAE6FF','#403294','#C0B6F2'],
    Automation: ['#E3FCEF','#006644','#57D9A3'],
    Optimisation: ['#DEEBFF','#0747A6','#B3D4FF'],
  }[t];
  const [bg, text, border] = cfg;
  return `<span class="opp-type-badge" style="display:inline-flex;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:800;background:${bg};color:${text};border:1px solid ${border};">${esc(t)}</span>`;
};


const getRecommendedTreatment = (o = {}) => {
  const explicit = String(o.recommended_treatment || '').trim();
  if (explicit) return explicit;
  return { AI: 'Apply AI', Automation: 'Automate', Optimisation: 'Optimise' }[getOpportunityType(o)] || 'Optimise';
};

const getAiSuitability = (o = {}) => {
  const explicit = String(o.ai_suitability || '').trim();
  if (explicit) return explicit;
  return getOpportunityType(o) === 'AI' ? 'High' : 'Not Required';
};

const aiSuitabilityBadge = (value) => {
  const v = String(value || 'Not Required');
  const cfg = {
    High: ['#EAE6FF','#403294','#C0B6F2'],
    Medium: ['#DEEBFF','#0747A6','#B3D4FF'],
    Low: ['#FFF0B3','#974F0C','#FFE380'],
    'Not Required': ['#F1F2F4','#44546F','#C7D1E0'],
  }[v] || ['#F1F2F4','#44546F','#C7D1E0'];
  return badge(`AI suitability: ${v}`, { bg: cfg[0], text: cfg[1], border: cfg[2] });
};

const treatmentBadge = (value) => {
  const v = String(value || 'Optimise');
  const cfg = {
    'Apply AI': ['#EAE6FF','#403294','#C0B6F2'],
    Automate: ['#E3FCEF','#006644','#57D9A3'],
    Optimise: ['#DEEBFF','#0747A6','#B3D4FF'],
  }[v] || ['#F1F2F4','#44546F','#C7D1E0'];
  return badge(v, { bg: cfg[0], text: cfg[1], border: cfg[2] });
};

const opportunityScore = (o = {}) => {
  const priority = { Critical: 30, High: 22, Medium: 14 }[o.priority] || 10;
  const impact = { 'Very High': 25, High: 18, Medium: 10 }[o.impact] || 7;
  const confidence = { High: 15, Moderate: 10, Low: 5 }[o.confidence] || 5;
  const effort = { Low: 15, Medium: 10, High: 5 }[o.effort] || 5;
  const value = { 'Very High': 15, High: 11, Medium: 7, Low: 3 }[o.value_potential] || 7;
  return Math.max(0, Math.min(100, priority + impact + confidence + effort + value));
};

const getRecommendedPriority = (o = {}) => {
  const explicit = String(o.recommended_priority || '').trim();
  if (['Do Now','Plan','Investigate','Monitor'].includes(explicit)) return explicit;
  const score = opportunityScore(o);
  if (score >= 75) return 'Do Now';
  if (score >= 60) return 'Plan';
  if (score >= 45) return 'Investigate';
  return 'Monitor';
};

const priorityActionBadge = (value) => {
  const v = String(value || 'Plan');
  const cfg = {
    'Do Now': ['#E3FCEF','#006644','#57D9A3'],
    'Plan': ['#DEEBFF','#0747A6','#B3D4FF'],
    'Investigate': ['#FFF0B3','#974F0C','#FFE380'],
    'Monitor': ['#F1F2F4','#44546F','#C7D1E0'],
  }[v] || ['#F1F2F4','#44546F','#C7D1E0'];
  return badge(v, { bg: cfg[0], text: cfg[1], border: cfg[2] });
};

const valuePotentialBadge = (value) => {
  const v = String(value || 'Medium');
  const cfg = {
    'Very High': ['#E3FCEF','#006644','#57D9A3'],
    'High': ['#E9F2FF','#0747A6','#85B8FF'],
    'Medium': ['#FFF0B3','#974F0C','#FFE380'],
    'Low': ['#F1F2F4','#44546F','#C7D1E0'],
  }[v] || ['#F1F2F4','#44546F','#C7D1E0'];
  return badge(`Value: ${v}`, { bg: cfg[0], text: cfg[1], border: cfg[2] });
};

const rankOpportunity = (o = {}) => opportunityScore(o);

const buildOpportunitySummary = (data) => {
  const opps = Array.isArray(data?.opportunities) ? data.opportunities : [];
  if (!opps.length) return '';
  const counts = { AI: 0, Automation: 0, Optimisation: 0 };
  const priorityCounts = { 'Do Now': 0, Plan: 0, Investigate: 0, Monitor: 0 };
  opps.forEach(o => {
    counts[getOpportunityType(o)] += 1;
    priorityCounts[getRecommendedPriority(o)] += 1;
  });
  const top = [...opps].sort((a,b) => rankOpportunity(b) - rankOpportunity(a))[0] || {};
  return `
<div class="opportunity-summary">
  <div class="summary-title-row">
    <div class="summary-heading-block">
      <div class="summary-kicker">Opportunity portfolio</div>
      <div class="summary-title">${opps.length} prioritised opportunities</div>
    </div>
    <div class="summary-top"><span>Highest priority</span><strong>${esc(top.title || '—')}</strong></div>
  </div>
  <div class="summary-toolbar">
    <div class="summary-stats" role="group" aria-label="Filter opportunity portfolio">
      <button class="summary-stat is-active" data-filter="All"><span>All</span><strong>${opps.length}</strong></button>
      <button class="summary-stat summary-stat-ai" data-filter="AI"><span class="summary-dot"></span><span>AI</span><strong>${counts.AI}</strong></button>
      <button class="summary-stat summary-stat-auto" data-filter="Automation"><span class="summary-dot"></span><span>Automation</span><strong>${counts.Automation}</strong></button>
      <button class="summary-stat summary-stat-opt" data-filter="Optimisation"><span class="summary-dot"></span><span>Optimisation</span><strong>${counts.Optimisation}</strong></button>
    </div>
    <div class="opportunity-filter-note"><span id="opp-visible-count">${opps.length}</span> shown · <strong>${priorityCounts['Do Now']}</strong> Do Now · <strong>${priorityCounts.Plan}</strong> Plan · <strong>${priorityCounts.Investigate}</strong> Investigate</div>
  </div>
</div>`;
};

// ── Priority / impact / effort badges ─────────────────────────────────────────

const badge = (text, scheme) =>
  `<span style="display:inline-flex;align-items:center;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${scheme.bg};color:${scheme.text};border:1px solid ${scheme.border};">${esc(text)}</span>`;

const priorityBadge = (p) => {
  const m = { Critical: ['#FFEBE6','#BF2600','#FF8F73'], High: ['#FFF0B3','#974F0C','#FFE380'], Medium: ['#DEEBFF','#0747A6','#B3D4FF'] };
  const [bg,text,border] = m[p] || m.Medium;
  return badge(p, {bg,text,border});
};
const impactBadge = (v) => {
  const m = { 'Very High': ['#FFEBE6','#BF2600','#FF8F73'], High: ['#FFF0B3','#974F0C','#FFE380'], Medium: ['#F4F5F7','#42526E','#DFE1E6'] };
  const [bg,text,border] = m[v] || m.Medium;
  return badge(`Impact: ${v}`, {bg,text,border});
};
const effortBadge = (v) => {
  const m = { Low: ['#E3FCEF','#006644','#57D9A3'], Medium: ['#FFF0B3','#974F0C','#FFE380'], High: ['#FFEBE6','#BF2600','#FF8F73'] };
  const [bg,text,border] = m[v] || m.Medium;
  return badge(`Effort: ${v}`, {bg,text,border});
};
const interventionBadge = (v) => {
  if (!v) return '';
  const cfg = {
    Optimize: ['#DEEBFF','#0747A6','#B3D4FF'],
    Automate: ['#E3FCEF','#006644','#ABF5D1'],
    Augment:  ['#EAE6FF','#403294','#C0B6F2'],
  }[v];
  if (!cfg) return '';
  const [bg, text, border] = cfg;
  return `<span style="display:inline-flex;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${bg};color:${text};border:1px solid ${border};">${esc(v)}</span>`;
};

const confidenceBadge = (v) => {
  const m = { High: ['#E3FCEF','#006644','#57D9A3'], Moderate: ['#FFF0B3','#974F0C','#FFE380'], Low: ['#F4F5F7','#42526E','#DFE1E6'] };
  const [bg,text,border] = m[v] || m.Low;
  return badge(`✓ ${v} confidence`, {bg,text,border});
};

// ── State type config ──────────────────────────────────────────────────────────

const STATE_COLORS = {
  Open:    ['#DEEBFF','#0747A6','#B3D4FF'],
  Active:  ['#E3FCEF','#006644','#57D9A3'],
  Waiting: ['#FFF0B3','#974F0C','#FFE380'],
  Review:  ['#EAE6FF','#5243AA','#998DD9'],
  Closed:  ['#F4F5F7','#42526E','#DFE1E6'],
};
const getStateColors = (t) => STATE_COLORS[t] || STATE_COLORS.Active;

// ── Styles ─────────────────────────────────────────────────────────────────────

const CSS = `
  html,body{margin:0;padding:0;box-sizing:border-box;}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  .ai-doc-header{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:14px 20px;margin-bottom:10px;display:flex;justify-content:space-between;align-items:center;}
  .ai-doc-header-left{display:flex;flex-direction:column;gap:4px;}
  .ai-doc-header-title{font-size:16px;font-weight:700;color:#172B4D;}
  .ai-doc-header-type{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:#0052CC;}
  .ai-doc-header-time{font-size:12px;color:#6B778C;}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Helvetica Neue',Arial,sans-serif;background:#F4F5F7;color:#172B4D;}
  .wrap{display:flex;flex-direction:column;gap:0;padding:10px;}
  .card{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:18px 20px;margin-bottom:10px;}
  .opportunity-list-card{padding:12px;background:#F8FAFC;border-color:#E4E7EC;}
  .hero{background:#fff;border:1px solid #E8E9EB;border-radius:10px;padding:20px 22px;position:relative;overflow:hidden;margin-bottom:10px;}
  .hero::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#6554C0,#0052CC,#00875A);}
  .eyebrow{font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#6554C0;margin-bottom:10px;display:flex;align-items:center;gap:6px;}
  .eyebrow::before{content:'';display:inline-block;width:16px;height:2px;background:#6554C0;border-radius:2px;}
  .headline{font-size:22px;font-weight:800;line-height:1.25;color:#172B4D;margin-bottom:10px;}
  .summary-text{font-size:14px;line-height:1.7;color:#42526E;padding-bottom:14px;border-bottom:1px solid #EBECF0;margin-bottom:14px;}
  .meta-row{display:flex;align-items:center;flex-wrap:wrap;gap:10px;}
  .potential-pill{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:999px;font-size:13px;font-weight:700;}
  .readiness-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:999px;font-size:12px;font-weight:700;}
  .section-hdr{display:flex;align-items:center;gap:10px;margin-bottom:16px;}
  .section-icon{width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;font-size:15px;}
  .section-title{font-size:17px;font-weight:700;color:#172B4D;}
  .signal-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px;}
  .signal-card{background:#FAFBFC;border:1px solid #EBECF0;border-radius:10px;padding:12px 14px;}
  .signal-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:3px;}
  .signal-value{font-size:22px;font-weight:800;color:#172B4D;line-height:1.15;margin-bottom:3px;}
  .signal-source{font-size:11px;font-weight:700;color:#6554C0;margin-bottom:5px;}
  .signal-why{font-size:12px;line-height:1.5;color:#42526E;}
  .opp-card{border:1px solid #EBECF0;border-radius:10px;background:#fff;margin-bottom:10px;overflow:hidden;}
  .opp-card:last-child{margin-bottom:0;}
  .opp-top{padding:14px 16px;border-bottom:1px solid #EBECF0;}
  .opp-num{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:5px;}
  .opp-title{font-size:16px;font-weight:700;color:#172B4D;margin-bottom:8px;}
  .opp-badges{display:flex;flex-wrap:wrap;gap:5px;margin-bottom:8px;}
  .rovo-tag{display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:6px;font-size:12px;font-weight:700;}
  .rovo-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
  .opp-body{display:grid;grid-template-columns:1fr 1fr;}
  .opp-cell{padding:12px 16px;border-right:1px solid #EBECF0;border-bottom:1px solid #EBECF0;}
  .opp-cell:nth-child(2n){border-right:none;}
  .opp-cell:nth-last-child(-n+2){border-bottom:none;}
  .cell-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .cell-text{font-size:13px;line-height:1.65;color:#42526E;}
  .decision-strip{display:grid;grid-template-columns:minmax(150px,.75fr) minmax(210px,1fr) minmax(220px,1.15fr);gap:0;border-top:1px solid #EBECF0;border-bottom:1px solid #EBECF0;background:#FAFBFC;}
  .decision-item{padding:11px 16px;border-right:1px solid #EBECF0;min-width:0;}
  .decision-item:last-child{border-right:none;}
  .decision-label{font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#6B778C;margin-bottom:6px;}
  .decision-value{font-size:12px;line-height:1.45;color:#172B4D;font-weight:700;}
  .decision-rationale{margin-top:5px;font-size:11px;line-height:1.45;color:#5E6C84;font-weight:400;}
  .value-execution-strip{display:grid;grid-template-columns:minmax(230px,1.2fr) minmax(230px,1.05fr) minmax(220px,.85fr);border-top:1px solid #EBECF0;background:#FFFFFF;}
  .value-execution-item{padding:12px 16px;border-right:1px solid #EBECF0;min-width:0;}
  .value-execution-item:last-child{border-right:none;}
  .value-execution-title{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:6px;}
  .value-execution-copy{font-size:12px;line-height:1.55;color:#42526E;}
  .value-execution-meta{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:6px;}
  .opp-actions{display:flex;gap:7px;flex-wrap:wrap;margin-top:7px;}
  .opp-action-btn{appearance:none;border:1px solid #B3D4FF;background:#F7FAFF;color:#0747A6;border-radius:7px;padding:6px 10px;font-size:11px;font-weight:800;cursor:pointer;}
  .opp-action-btn:hover{background:#DEEBFF;border-color:#85B8FF;}
  .opp-action-btn.secondary{border-color:#DFE1E6;background:#FAFBFC;color:#44546F;}
  .opp-action-btn.secondary:hover{background:#F1F2F4;}
  .config-row{padding:10px 16px;background:#F8F9FF;border-top:1px solid #EBECF0;font-size:12px;line-height:1.6;color:#0747A6;}
  .config-row strong{font-weight:700;}
  .evidence-row{padding:10px 16px;background:#FAFBFC;border-top:1px solid #EBECF0;}
  .evidence-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:5px;}
  .evidence-item{font-size:12px;color:#42526E;line-height:1.5;padding:3px 0 3px 10px;border-left:2px solid #EBECF0;margin-bottom:3px;}
  .group-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;}
  .group-card{border:1px solid #EBECF0;border-radius:10px;padding:14px;background:#FAFBFC;}
  .group-title{font-size:14px;font-weight:700;color:#172B4D;margin-bottom:6px;}
  .group-summary{font-size:13px;line-height:1.6;color:#42526E;margin-bottom:10px;}
  .link-pills{display:flex;flex-wrap:wrap;gap:5px;}
  .link-pill{padding:3px 9px;border-radius:999px;background:#EAE6FF;color:#5243AA;font-size:11px;font-weight:700;}
  .phase-list{display:flex;flex-direction:column;gap:8px;}
  .phase{border:1px solid #EBECF0;border-radius:8px;background:#FAFBFC;overflow:hidden;}
  .phase-top{display:flex;align-items:center;gap:12px;padding:12px 14px;}
  .phase-num{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;color:#fff;flex-shrink:0;}
  .phase-meta{flex:1;}
  .phase-name{font-size:14px;font-weight:700;color:#172B4D;}
  .phase-focus{font-size:12px;color:#42526E;margin-top:2px;}
  .phase-products{display:flex;flex-wrap:wrap;gap:5px;padding:0 14px 10px 58px;}
  .phase-actions{padding:0 14px 12px 58px;}
  .phase-action{font-size:12px;color:#42526E;line-height:1.55;padding:3px 0 3px 10px;border-left:2px solid #DFE1E6;margin-bottom:3px;}
  .flow-row{display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-bottom:16px;}
  .flow-step{display:inline-flex;align-items:center;gap:6px;background:#F4F5F7;border:1px solid #DFE1E6;border-radius:999px;padding:7px 14px;font-size:12px;font-weight:700;color:#172B4D;}
  .flow-arrow{font-size:14px;color:#97A0AF;}
  .state-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;}
  .state-card{border:1px solid #EBECF0;border-radius:10px;padding:14px;display:flex;flex-direction:column;gap:6px;}
  .state-top{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:2px;}
  .state-name{font-size:14px;font-weight:700;color:#172B4D;}
  .state-type{display:inline-flex;padding:3px 9px;border-radius:999px;font-size:10px;font-weight:700;}
  .state-purpose{font-size:13px;line-height:1.6;color:#42526E;}
  .state-rule{font-size:11px;color:#42526E;padding:3px 8px;background:#F4F5F7;border-radius:4px;border-left:2px solid #DFE1E6;margin-bottom:2px;}
  .state-rovo{display:flex;align-items:center;gap:6px;margin-top:4px;padding:6px 10px;border-radius:6px;font-size:12px;font-weight:600;}

  .opportunity-summary{background:#FFFFFF;border:1px solid #E4E7EC;border-radius:12px;padding:16px 18px;margin-bottom:10px;box-shadow:0 1px 2px rgba(9,30,66,.03);}
  .summary-title-row{display:flex;justify-content:space-between;align-items:center;gap:18px;margin-bottom:12px;}
  .summary-heading-block{min-width:0;}
  .summary-kicker{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.1em;color:#0C66E4;margin-bottom:3px;}
  .summary-title{font-size:18px;line-height:1.2;font-weight:800;color:#172B4D;}
  .summary-top{min-width:240px;max-width:38%;padding:7px 10px;border-radius:8px;background:#F8FAFC;border:1px solid #E4E7EC;display:flex;align-items:center;gap:8px;}
  .summary-top span{font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;white-space:nowrap;}
  .summary-top strong{font-size:12px;line-height:1.3;color:#172B4D;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .summary-toolbar{display:flex;align-items:center;justify-content:space-between;gap:12px;padding-top:11px;border-top:1px solid #F0F1F3;}
  .summary-stats{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;}
  .summary-stat{appearance:none;display:inline-flex;align-items:center;gap:6px;border:1px solid #DFE1E6;background:#FFFFFF;border-radius:999px;padding:6px 10px;cursor:pointer;color:#44546F;transition:.15s ease;font:inherit;}
  .summary-stat:hover{border-color:#85B8FF;background:#F8FBFF;}
  .summary-stat.is-active{border-color:#0C66E4;background:#E9F2FF;color:#0747A6;box-shadow:none;}
  .summary-stat span{font-size:11px;font-weight:700;color:inherit;}
  .summary-stat strong{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 6px;border-radius:999px;background:#F1F2F4;font-size:10px;line-height:1;color:#44546F;}
  .summary-stat.is-active strong{background:#FFFFFF;color:#0747A6;}
  .summary-dot{width:7px;height:7px;border-radius:50%;background:#97A0AF;flex:0 0 auto;}
  .summary-stat-ai .summary-dot{background:#6554C0}.summary-stat-auto .summary-dot{background:#00875A}.summary-stat-opt .summary-dot{background:#0C66E4}
  .opportunity-filter-note{font-size:11px;color:#6B778C;white-space:nowrap;}
  .opp-card{position:relative;box-shadow:0 1px 2px rgba(9,30,66,.03);transition:box-shadow .15s ease,border-color .15s ease;}
  .opp-card:hover{border-color:#C7D1E0;box-shadow:0 4px 14px rgba(9,30,66,.07);}
  .opp-card[data-opportunity-type="AI"]{border-left:4px solid #6554C0;}
  .opp-card[data-opportunity-type="Automation"]{border-left:4px solid #00875A;}
  .opp-card[data-opportunity-type="Optimisation"]{border-left:4px solid #0C66E4;}
  .opp-top{padding:14px 16px 12px;}
  .opp-num{font-size:9px;font-weight:800;letter-spacing:.09em;text-transform:uppercase;color:#6B778C;margin-bottom:4px;}
  .opp-title{font-size:17px;font-weight:800;color:#172B4D;margin-bottom:8px;}
  .opp-badges{display:flex;flex-wrap:wrap;gap:5px;align-items:center;}
  .opp-card.is-hidden{display:none;}

  @media (max-width:700px){.opp-body,.state-grid,.decision-strip,.value-execution-strip{grid-template-columns:1fr;}.decision-item{border-right:none;border-bottom:1px solid #EBECF0;}.decision-item:last-child{border-bottom:none;}.opp-cell{border-right:none;}.summary-title-row,.summary-toolbar{flex-direction:column;align-items:stretch}.summary-top{max-width:100%;width:100%;min-width:0}.summary-stats{display:flex}.opportunity-filter-note{white-space:normal;}}
  @media print{body{background:#fff;}.card,.hero,.opportunity-summary{break-inside:avoid;}.summary-stats{display:none;}.opp-card.is-hidden{display:block!important;}}
`;

// ── Builders ───────────────────────────────────────────────────────────────────

const buildDocHeader = (modelName, heading = 'AI, Automation & Optimisation Opportunities') => {
  const timestamp = new Date().toLocaleString('en-US', { 
    year: 'numeric', month: 'short', day: 'numeric', 
    hour: '2-digit', minute: '2-digit', hour12: true 
  });
  return `
<div class="ai-doc-header">
  <div class="ai-doc-header-left">
    <div class="ai-doc-header-type">${esc(heading)}</div>
    <div class="ai-doc-header-title">${esc(modelName || 'Process Model')}</div>
  </div>
  <div class="ai-doc-header-time">${timestamp}</div>
</div>`;
};

const buildHero = (data) => {
  const s = data?.summary || {};
  const meta = getPlatformMeta(data);
  const readiness = getReadiness(data);
  const rationale = getReadinessRationale(data);
  const potConfig = {
    Transformational: ['#6554C0','#fff'], High: ['#0052CC','#fff'],
    Moderate: ['#FF8B00','#fff'], Targeted: ['#42526E','#fff'], Limited: ['#97A0AF','#fff'],
  };
  const [potBg, potText] = potConfig[s.ai_potential] || potConfig.High;

  const readinessConfig = {
    High:   ['#E3FCEF','#006644','#57D9A3'],
    Medium: ['#FFF0B3','#974F0C','#FFE380'],
    Low:    ['#FFEBE6','#BF2600','#FF8F73'],
  };
  const [rBg, rText, rBorder] = readinessConfig[readiness] || readinessConfig.Medium;
  const rawHeadline = s.headline || `${meta.opportunityHeading} for ${meta.platformName}`;
  const headline = meta.isSnow
    ? String(rawHeadline)
        .replace(/^ServiceNow\s+AI\s+Opportunities\s+for\s+ServiceNow$/i, 'AI Opportunities for ServiceNow')
        .replace(/^ServiceNow\s+(.+?)\s+for\s+ServiceNow$/i, '$1 for ServiceNow')
    : rawHeadline;
  const summary = s.executive_summary || rationale || '';

  return `
<div class="hero">
  <div class="eyebrow">${esc(meta.opportunityHeading)}</div>
  <div class="headline">${esc(headline)}</div>
  ${summary ? `<div class="summary-text">${esc(summary)}</div>` : ''}
  <div class="meta-row">
    <span class="potential-pill" style="background:${potBg};color:${potText};">
      AI Potential: ${esc(s.ai_potential || 'High')} &nbsp;·&nbsp; ${esc(s.confidence || 'Moderate')} confidence
    </span>
    <span class="readiness-pill" style="background:${rBg};color:${rText};border:1px solid ${rBorder};">
      ${esc(meta.readinessLabel)}: ${esc(readiness)}
    </span>
  </div>
  ${rationale && rationale !== summary ? `<div style="margin-top:10px;font-size:13px;color:#42526E;line-height:1.6;">${esc(rationale)}</div>` : ''}
</div>`;
}

const buildSignals = (data) => {
  const signals = data?.headline_signals || [];
  if (!signals.length) return '';
  const cards = signals.map(s => `
    <div class="signal-card">
      <div class="signal-label">${esc(s.label)}</div>
      <div class="signal-value">${esc(s.value)}</div>
      <div class="signal-source">${esc(s.source)}</div>
      <div class="signal-why">${esc(s.why_it_matters)}</div>
    </div>`).join('');
  return `
<div class="card">
  <div class="section-hdr">
    <div class="section-icon" style="background:#6554C0;">📈</div>
    <div class="section-title">Headline Signals</div>
  </div>
  <div class="signal-grid">${cards}</div>
</div>`;
};

const buildOpportunities = (data, { interactive = true } = {}) => {
  const opps   = data?.opportunities || [];
  const groups = data?.opportunity_groups || [];
  if (!opps.length) return '';
  const meta = getPlatformMeta(data);

  const oppCards = opps.map((o, i) => {
    const product = o.platform_product || o.rovo_product;
    const pc  = getPlatformProduct(product);
    const evidence = (o.evidence_points || []).map(e =>
      `<div class="evidence-item">${esc(e)}</div>`).join('');
    const howHelps = o.how_platform_helps || o.how_rovo_helps || '';
    const trigger = o.platform_trigger || o.rovo_trigger || '';
    const configNote = o.platform_config_note || o.rovo_config_note || '';
    const treatment = getRecommendedTreatment(o);
    const aiSuitability = getAiSuitability(o);
    const aiSuitabilityRationale = o.ai_suitability_rationale || (getOpportunityType(o) === 'AI'
      ? 'AI is appropriate where the decision depends on classification, prediction, unstructured content, knowledge retrieval or judgement at scale.'
      : 'AI is not required for this opportunity; a process or deterministic automation intervention is more appropriate.');
    const recommendedCapability = o.recommended_platform_capability || product || (getOpportunityType(o) === 'Optimisation' ? 'Process Change' : meta.defaultProduct);
    const executionPriority = getRecommendedPriority(o);
    const score = opportunityScore(o);
    const valuePotential = o.value_potential || (o.impact === 'Very High' ? 'Very High' : o.impact || 'Medium');
    const valueDriver = o.value_driver || 'Operational improvement';
    const valueHypothesis = o.value_hypothesis || o.expected_benefit || "Validate the value hypothesis against the customer's operating and cost assumptions.";
    const priorityRationale = o.priority_rationale || `Priority reflects ${String(o.impact || 'medium').toLowerCase()} impact, ${String(o.effort || 'medium').toLowerCase()} effort and ${String(o.confidence || 'moderate').toLowerCase()} confidence.`;
    const timeHorizon = o.time_horizon || (executionPriority === 'Do Now' ? '0-30 days' : executionPriority === 'Plan' ? '30-60 days' : '60-90 days');
    const valueScenario = o.value_simulation_scenario || 'none';

    return `
    <div class="opp-card" data-opportunity-type="${esc(getOpportunityType(o))}">
      <div class="opp-top">
        <div class="opp-num">Opportunity ${i + 1}</div>
        <div class="opp-title">${esc(o.title)}</div>
        <div class="opp-badges">
          ${priorityBadge(o.priority)}
          ${impactBadge(o.impact)}
          ${effortBadge(o.effort)}
          ${confidenceBadge(o.confidence)}
          ${priorityActionBadge(executionPriority)}
          ${valuePotentialBadge(valuePotential)}
          ${badge(`Score ${score}/100`, {bg:'#F1F2F4',text:'#44546F',border:'#C7D1E0'})}
          ${opportunityTypeBadge(o)}
          ${o.ai_use_case && o.ai_use_case !== 'Not Applicable' ? `<span style="display:inline-flex;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700;background:#F4F5F7;color:#42526E;border:1px solid #DFE1E6;">${esc(o.ai_use_case)}</span>` : ''}
        </div>
        <div class="rovo-tag" style="background:${pc.bg};color:${pc.text};border:1px solid ${pc.border};margin-top:6px;width:fit-content;">
          <span class="rovo-dot" style="background:${pc.dot};"></span>
          ${esc(pc.label)}
        </div>
      </div>
      <div class="decision-strip">
        <div class="decision-item">
          <div class="decision-label">Recommended treatment</div>
          <div class="decision-value">${treatmentBadge(treatment)}</div>
        </div>
        <div class="decision-item">
          <div class="decision-label">AI suitability</div>
          <div class="decision-value">${aiSuitabilityBadge(aiSuitability)}</div>
          <div class="decision-rationale">${esc(aiSuitabilityRationale)}</div>
        </div>
        <div class="decision-item">
          <div class="decision-label">Recommended platform capability</div>
          <div class="decision-value">${esc(recommendedCapability)}</div>
        </div>
      </div>
      <div class="value-execution-strip">
        <div class="value-execution-item">
          <div class="value-execution-title">Value hypothesis</div>
          <div class="value-execution-meta">${valuePotentialBadge(valuePotential)}${badge(valueDriver, {bg:'#F4F5F7',text:'#44546F',border:'#DFE1E6'})}</div>
          <div class="value-execution-copy">${esc(valueHypothesis)}</div>
        </div>
        <div class="value-execution-item">
          <div class="value-execution-title">Execution priority</div>
          <div class="value-execution-meta">${priorityActionBadge(executionPriority)}${badge(timeHorizon, {bg:'#F4F5F7',text:'#44546F',border:'#DFE1E6'})}</div>
          <div class="value-execution-copy">${esc(priorityRationale)}</div>
        </div>
        <div class="value-execution-item">
          <div class="value-execution-title">Next step</div>
          <div class="value-execution-copy">Validate value, then carry the opportunity into execution planning.</div>
          ${interactive ? `<div class="opp-actions">
            ${valueScenario !== 'none' ? `<button class="opp-action-btn" data-opp-action="value" data-opp-index="${i}">Model value</button>` : ''}
            <button class="opp-action-btn secondary" data-opp-action="execution" data-opp-index="${i}">Execution strategy</button>
          </div>` : ''}
        </div>
      </div>
      <div class="opp-body">
        <div class="opp-cell"><div class="cell-label">Problem signal</div><div class="cell-text">${esc(o.problem_signal)}</div></div>
        <div class="opp-cell"><div class="cell-label">Why this intervention</div><div class="cell-text">${howHelps ? esc(howHelps) : esc(aiSuitabilityRationale)}</div></div>
        <div class="opp-cell"><div class="cell-label">Trigger</div><div class="cell-text">${esc(trigger)}</div></div>
        <div class="opp-cell"><div class="cell-label">Expected benefit</div><div class="cell-text">${esc(o.expected_benefit)}<div style="margin-top:8px;font-size:11px;color:#6B778C;"><strong>Owner:</strong> ${esc(o.owner_hint)}</div></div></div>
      </div>
      ${configNote ? `<div class="config-row"><strong>Config:</strong> ${esc(configNote)}</div>` : ''}
      ${evidence ? `<div class="evidence-row"><div class="evidence-label">Evidence</div>${evidence}</div>` : ''}
    </div>`;
  }).join('');

  const groupCards = groups.map(g => {
    const pills = (g.linked_opportunities || []).map(l =>
      `<span class="link-pill">${esc(l)}</span>`).join('');
    return `
    <div class="group-card">
      <div class="group-title">${esc(g.group_name)}</div>
      <div class="group-summary">${esc(g.summary)}</div>
      <div class="link-pills">${pills}</div>
    </div>`;
  }).join('');

  return `
<div class="card opportunity-list-card">
  ${oppCards}
</div>
${groups.length ? `
<div class="card">
  <div class="section-hdr">
    <div class="section-icon" style="background:#0747A6;">⋯</div>
    <div class="section-title">Opportunity Themes</div>
  </div>
  <div class="group-grid">${groupCards}</div>
</div>` : ''}`;
};

const buildSequence = (data) => {
  const phases = data?.implementation_sequence?.phases || [];
  if (!phases.length) return '';

  const phaseColors = ['#6554C0', '#0052CC', '#00875A', '#FF8B00'];
  const phaseItems = phases.map((p, i) => {
    const products = p.platform_products_used || p.rovo_products_used || [];
    const rc = getPlatformProduct((products || [])[0]);
    const productTags = (products || []).map(prod => {
      const pc = getPlatformProduct(prod);
      return `<span style="display:inline-flex;align-items:center;gap:4px;padding:3px 9px;border-radius:999px;font-size:11px;font-weight:700;background:${pc.bg};color:${pc.text};border:1px solid ${pc.border};">
        <span style="width:6px;height:6px;border-radius:50%;background:${pc.dot};"></span>${esc(pc.label)}
      </span>`;
    }).join('');

    const buildLabel = p.requires_build === false
      ? `<span style="display:inline-flex;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;background:#E3FCEF;color:#006644;border:1px solid #57D9A3;">No build required</span>`
      : '';

    const actions = (p.actions || []).map(a =>
      `<div class="phase-action">${esc(a)}</div>`).join('');

    return `
    <div class="phase">
      <div class="phase-top">
        <div class="phase-num" style="background:${phaseColors[i % phaseColors.length]};">${i + 1}</div>
        <div class="phase-meta">
          <div class="phase-name">${esc(p.phase)} ${buildLabel}</div>
          <div class="phase-focus">${esc(p.focus)}</div>
        </div>
      </div>
      ${productTags ? `<div class="phase-products">${productTags}</div>` : ''}
      ${actions ? `<div class="phase-actions">${actions}</div>` : ''}
    </div>`;
  }).join('');

  return `
<div class="card">
  <div class="section-hdr">
    <div class="section-icon" style="background:#FF8B00;">→</div>
    <div class="section-title">Implementation Sequence</div>
  </div>
  <div class="phase-list">${phaseItems}</div>
</div>`;
};

const buildTargetState = (data) => {
  const tws    = data?.target_workflow_state || {};
  const states = tws.states || [];
  const flow   = tws.preferred_flow || [];
  if (!states.length) return '';

  const flowSteps = flow.map((f, i) => {
    const isLast = i === flow.length - 1;
    const stateData = states.find(s => s.state_name === f);
    const hasAi = stateData?.ai_decision_point;
    const product = stateData?.platform_product || stateData?.rovo_product;
    const rc = hasAi ? getPlatformProduct(product) : null;
    return `
    <span class="flow-step" ${rc ? `style="border-color:${rc.border};background:${rc.bg};"` : ''}>
      ${rc ? `<span style="width:7px;height:7px;border-radius:50%;background:${rc.dot};"></span>` : ''}
      ${esc(f)}
    </span>${!isLast ? '<span class="flow-arrow">→</span>' : ''}`;
  }).join('');

  const stateCards = states.map(s => {
    const [bg, text, border] = getStateColors(s.state_type);
    const product = s.platform_product || s.rovo_product;
    const action = s.platform_action || s.rovo_action;
    const rc = s.ai_decision_point ? getPlatformProduct(product) : null;
    return `
    <div class="state-card" style="background:${bg}20;border-color:${border};">
      <div class="state-top">
        <div class="state-name">${esc(s.state_name)}</div>
        <span class="state-type" style="background:${bg};color:${text};border:1px solid ${border};">${esc(s.state_type)}</span>
      </div>
      <div class="state-purpose">${esc(s.purpose)}</div>
      <div class="state-rule"><strong>Enter:</strong> ${esc(s.entry_rule)}</div>
      <div class="state-rule"><strong>Exit:</strong> ${esc(s.exit_rule)}</div>
      ${rc ? `
      <div class="state-rovo" style="background:${rc.bg};color:${rc.text};">
        <span class="rovo-dot" style="background:${rc.dot};"></span>
        ${esc(rc.label)}: ${esc(action)}
      </div>` : ''}
    </div>`;
  }).join('');

  return `
<div class="card">
  <div class="section-hdr">
    <div class="section-icon" style="background:#00875A;">◎</div>
    <div class="section-title">Target Workflow State</div>
  </div>
  <div style="font-size:13px;line-height:1.65;color:#42526E;margin-bottom:16px;">${esc(tws.summary || '')}</div>
  <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;margin-bottom:8px;">Preferred flow</div>
  <div class="flow-row">${flowSteps}</div>
  <div class="state-grid">${stateCards}</div>
</div>`;
};

// ── Legend ─────────────────────────────────────────────────────────────────────

const buildLegend = (data) => {
  const meta = getPlatformMeta(data);
  const names = meta.isSnow
    ? ['Now Assist', 'Predictive Intelligence', 'Virtual Agent', 'Flow Designer', 'Automation Engine', 'Assignment Rules', 'SLA/Performance Analytics']
    : Object.keys(ROVO);
  const items = names.map((name) => {
    const c = getPlatformProduct(name);
    return `<span style="display:inline-flex;align-items:center;gap:5px;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${c.bg};color:${c.text};border:1px solid ${c.border};">
      <span style="width:6px;height:6px;border-radius:50%;background:${c.dot};"></span>${esc(c.label)}
    </span>`;
  }).join('');

  const readiness = getReadiness(data);
  const rationale = getReadinessRationale(data);
  const readinessConfig = {
    High:   ['#E3FCEF','#006644','#57D9A3'],
    Medium: ['#FFF0B3','#974F0C','#FFE380'],
    Low:    ['#FFEBE6','#BF2600','#FF8F73'],
  };
  const [rBg, rText, rBorder] = readinessConfig[readiness] || readinessConfig.Medium;
  const readinessPill = readiness
    ? `<span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;font-size:11px;font-weight:700;background:${rBg};color:${rText};border:1px solid ${rBorder};">${esc(meta.readinessLabel)}: ${esc(readiness)}</span>`
    : '';

  return `
<div style="margin-bottom:10px;padding:10px 14px;background:#fff;border:1px solid #E8E9EB;border-radius:10px;display:flex;align-items:center;flex-wrap:wrap;gap:10px;">
  <span style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6B778C;white-space:nowrap;">${esc(meta.productLabel)}</span>
  ${items}
</div>`;
}

// ── Main exports ───────────────────────────────────────────────────────────────

export const renderAIIntelligenceHTML = (json, modelTitle = '') => {
  if (!json) return '<p style="padding:20px;color:#6B778C;">No data available.</p>';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-opportunity-hub-v3"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(getPlatformMeta(json).opportunityHeading)}${modelTitle ? ` — ${esc(modelTitle)}` : ''}</title>
<style>${CSS}</style>
</head>
<body>
<div class="wrap">
${buildHero(json)}
${buildLegend(json)}
${buildOpportunitySummary(json)}
${buildOpportunities(json)}
${buildSequence(json)}
${buildTargetState(json)}
</div>
<script>
(function(){
  function resize(){
    try { parent.postMessage({type:'rovo-height',height:document.documentElement.scrollHeight||document.body.scrollHeight}, '*'); } catch(e) {}
  }
  function getData(){
    try { var el=document.getElementById('__qa_data__'); return el ? JSON.parse(el.textContent||'{}') : {}; } catch(e) { return {}; }
  }
  function applyFilter(filter){
    var cards=[].slice.call(document.querySelectorAll('.opp-card[data-opportunity-type]'));
    var visible=0;
    cards.forEach(function(card){
      var show=filter==='All' || card.getAttribute('data-opportunity-type')===filter;
      card.classList.toggle('is-hidden', !show);
      if(show) visible++;
    });
    document.querySelectorAll('.summary-stat[data-filter]').forEach(function(btn){
      btn.classList.toggle('is-active', btn.getAttribute('data-filter')===filter);
    });
    var counter=document.getElementById('opp-visible-count'); if(counter) counter.textContent=visible;
    setTimeout(resize, 0);
  }
  document.addEventListener('click',function(e){
    var filterBtn=e.target.closest && e.target.closest('.summary-stat[data-filter]');
    if(filterBtn){ applyFilter(filterBtn.getAttribute('data-filter')); return; }
  });
  window.addEventListener('load', resize);
})();
</script>
${embeddedJsonScript(json)}
</body>
</html>`;
};

export const buildAIIntelligenceExportHTML = (json, modelTitle = '') => {
  if (!json) return '<p style="padding:20px;color:#6B778C;">No data available.</p>';

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta name="renderer-version" content="v2026-opportunity-hub-v3"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>${esc(getPlatformMeta(json).opportunityHeading)}${modelTitle ? ` — ${esc(modelTitle)}` : ''}</title>
<style>${CSS}</style>
</head>
<body>
<div class="wrap">
${buildDocHeader(modelTitle, getPlatformMeta(json).opportunityHeading)}
${buildHero(json)}
${buildLegend(json)}
${buildOpportunitySummary(json)}
${buildOpportunities(json, { interactive: false })}
${buildSequence(json)}
${buildTargetState(json)}
</div>
<script>
(function(){
  function resize(){
    try { parent.postMessage({type:'rovo-height',height:document.documentElement.scrollHeight||document.body.scrollHeight}, '*'); } catch(e) {}
  }
  function getData(){
    try { var el=document.getElementById('__qa_data__'); return el ? JSON.parse(el.textContent||'{}') : {}; } catch(e) { return {}; }
  }
  function applyFilter(filter){
    var cards=[].slice.call(document.querySelectorAll('.opp-card[data-opportunity-type]'));
    var visible=0;
    cards.forEach(function(card){
      var show=filter==='All' || card.getAttribute('data-opportunity-type')===filter;
      card.classList.toggle('is-hidden', !show);
      if(show) visible++;
    });
    document.querySelectorAll('.summary-stat[data-filter]').forEach(function(btn){
      btn.classList.toggle('is-active', btn.getAttribute('data-filter')===filter);
    });
    var counter=document.getElementById('opp-visible-count'); if(counter) counter.textContent=visible;
    setTimeout(resize, 0);
  }
  document.addEventListener('click',function(e){
    var filterBtn=e.target.closest && e.target.closest('.summary-stat[data-filter]');
    if(filterBtn){ applyFilter(filterBtn.getAttribute('data-filter')); return; }
  });
  window.addEventListener('load', resize);
})();
</script>
${embeddedJsonScript(json)}
</body>
</html>`;
};

